// src/context/contextApi.js
//
// API service for the context library (/api/v1/context/*): the RAG documents
// corpus, the raw config singletons + tenant-profile, personas, the Site
// Manager and the brand voice.
//
// Tenant scope: every call passes an explicit ?tenant=<id> and sets
// suppressTenantHeader so the query param wins over any stale X-Tenant-Id in
// localStorage. Mirrors admin/runsApi.js.
import { apiFetch } from '../auth/apiFetch.js';
import { parseApiError } from './parseApiError.js';

const CONTEXT = '/api/v1/context';

function withTenant(path, tenant) {
  if (!tenant) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}tenant=${encodeURIComponent(tenant)}`;
}

// withTenant for the calls that carry more than one param (`tenant` +
// `language` + `include_disabled`). Empty/undefined values are DROPPED rather
// than sent blank: `?language=` is not a valid language and the backend's
// strict-query layer answers 422 INVALID_LANGUAGE for it.
function withQuery(path, params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

// ── Documents (RAG corpus) ──────────────────────────────────────────────────

/** → { documents: DocumentInfo[], total_chunks } */
export async function listDocuments({ tenant } = {}) {
  const res = await apiFetch(withTenant(`${CONTEXT}/documents`, tenant), {
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** Upload (admin). multipart/form-data, file in `file`. → 202 { status, filename } */
export async function uploadDocument({ tenant, file }) {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(withTenant(`${CONTEXT}/documents`, tenant), {
    method: 'POST',
    body: form,
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** Reindex failed/stale (admin). → 202. Submit bare filename. */
export async function reindexDocument({ tenant, filename }) {
  const res = await apiFetch(
    withTenant(`${CONTEXT}/documents/${encodeURIComponent(filename)}/reindex`, tenant),
    { method: 'POST', suppressTenantHeader: true },
  );
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** Delete (admin) — removes file + vectors + manifest. → 200. */
export async function deleteDocument({ tenant, filename }) {
  const res = await apiFetch(
    withTenant(`${CONTEXT}/documents/${encodeURIComponent(filename)}`, tenant),
    { method: 'DELETE', suppressTenantHeader: true },
  );
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

// ── Context config (singletons + tenant-profile) — raw editing ──────────────
// GETs return verbatim `raw` + `etag`. ALWAYS write { raw } (never { content } —
// the server re-dumps YAML for { content } and strips comments).

export const SINGLETON_NAMES = ['website-urls', 'internal-links', 'tenant-settings', 'model-config'];

/** Read a singleton (any member). → { name, etag, raw, content } */
export async function getSingleton({ tenant, name }) {
  const res = await apiFetch(
    withTenant(`${CONTEXT}/singletons/${encodeURIComponent(name)}`, tenant),
    { suppressTenantHeader: true },
  );
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * Write a singleton (admin). Sends raw bytes verbatim + If-Match (when etag
 * supplied). → { name, etag, created }
 */
export async function putSingleton({ tenant, name, raw, etag }) {
  const res = await apiFetch(
    withTenant(`${CONTEXT}/singletons/${encodeURIComponent(name)}`, tenant),
    {
      method: 'PUT',
      body: JSON.stringify({ raw }),
      headers: etag ? { 'If-Match': etag } : {},
      suppressTenantHeader: true,
    },
  );
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** Read tenant-profile.md (any member). → { front_matter, body, raw, etag } */
export async function getTenantProfile({ tenant }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/tenant-profile`, tenant), {
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** Write tenant-profile.md (admin). raw markdown only. → { etag, created } */
export async function putTenantProfile({ tenant, raw, etag }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/tenant-profile`, tenant), {
    method: 'PUT',
    body: JSON.stringify({ raw }),
    headers: etag ? { 'If-Match': etag } : {},
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

// ── Personas (listing + toggle + structured editor) ─────────────────────────
//
//   GET  /personas                        any member — ENABLED rows only
//   GET  /personas?include_disabled=true  ADMIN — every row + state/engine_writable/halves
//   PUT  /personas/{id}/enabled           ADMIN — one persona (answer carries NO provenance)
//   GET  /personas/{id}/sections          any member — both halves as fields + labels
//   PUT  /personas/{id}/sections          ADMIN — save/create; ALWAYS stamps `human` (frozen)
//
// ⛔ The whole-file PUT /personas/{id} (body `{raw}`) is SUPERADMIN-only on the
// active backend, so this app no longer calls it at all: there is no raw editor
// left, and the "may the AI still update it" flag that the previous FE tried to
// smuggle into that body never existed on any backend branch.

/**
 * List personas. → ContextEntityResponse[] =
 * { id, source, label, market, enabled, state, engine_writable, halves }
 *
 * ⚠️ The last three are ADMIN-ONLY and cost the backend a ledger query it will
 * not spend on the ordinary reader: this same endpoint feeds the brief dropdown,
 * so a non-admin gets null for all three, and so does a request without
 * `includeDisabled`. A dead ledger ALSO degrades to nulls rather than failing
 * the list — so null means "not told", never "not frozen". No UI reads these
 * three any more: nothing in the app updates personas automatically.
 *
 * `includeDisabled` additionally requires admin on this tenant (403
 * TENANT_ADMIN_REQUIRED otherwise); without it the server returns enabled rows
 * only and every `enabled` reads true — which is why the management screen must
 * pass it rather than inferring "off" from an absent row.
 *
 * ⚠️ `market` is language-INVARIANT (`b2b` | `b2c` | `any` | null) — it is read from the
 * persona's base half, which is why grouping on it survives `language: 'en'`.
 * An old-format persona declares none and returns null.
 */
export async function listPersonas({ tenant, includeDisabled = false, language } = {}) {
  const path = withQuery(`${CONTEXT}/personas`, {
    tenant,
    include_disabled: includeDisabled ? 'true' : undefined,
    language,
  });
  const res = await apiFetch(path, { suppressTenantHeader: true });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * Switch ONE persona on or off for this tenant (admin). → the updated row.
 *
 * ⚠️ 409 CANNOT_DISABLE_LAST_PERSONA when the call would leave the tenant with
 * zero enabled personas — `persona` is a required brief field. There is no
 * `force` and no `details`: surface the message and leave the toggle where it
 * was.
 *
 * ⛔ NO ETag on this route — last-write-wins, by backend decision. Re-list
 * after a batch of switches rather than trusting the answers you collected.
 */
export async function setPersonaEnabled({ tenant, id, enabled, language }) {
  const path = withQuery(`${CONTEXT}/personas/${encodeURIComponent(id)}/enabled`, {
    tenant, language,
  });
  const res = await apiFetch(path, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * One persona as fields (any member). → { id, source, etag, etag_en, market,
 * it, en|null, labels:{it,en}, state, engine_writable, halves }.
 * 409 PERSONA_LEGACY_FORMAT: the file predates the 4-section model — no editor.
 */
export async function getPersonaSections({ tenant, id }) {
  const path = withQuery(`${CONTEXT}/personas/${encodeURIComponent(id)}/sections`, { tenant });
  const res = await apiFetch(path, { suppressTenantHeader: true });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * Save — or create — one persona (admin). body = {market: b2b|b2c|any, it, en|null}.
 * `etag` → If-Match (an edit). `ifNoneMatch: true` → If-None-Match: * (a CREATE: 412 PERSONA_ALREADY_EXISTS,
 * `details.id`, when the id resolves as a tenant OR global persona — nothing written). Never both:
 * `ifNoneMatch` wins. `body` is sent VERBATIM as the caller built it.
 * → { id, type, source, etag, created, strict_mode_activated, halves_written, etag_en }
 */
export async function putPersonaSections({ tenant, id, body, etag, ifNoneMatch = false }) {
  const path = withQuery(`${CONTEXT}/personas/${encodeURIComponent(id)}/sections`, { tenant });
  const headers = ifNoneMatch ? { 'If-None-Match': '*' } : (etag ? { 'If-Match': etag } : {});
  const res = await apiFetch(path, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers,
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

// ── Documents: download ─────────────────────────────────────────────────────

/**
 * Download the stored file (any member). → Blob.
 *
 * A plain <a href> cannot carry the bearer token, so the bytes come through
 * apiFetch and the caller turns them into an object URL (utils/blobDownload.js).
 * ⛔ Never 402/503: a download is a read, neither credit- nor retirement-gated.
 * The filename is already known to the caller (the row), so Content-Disposition
 * is not parsed here.
 */
export async function downloadDocument({ tenant, filename }) {
  const res = await apiFetch(
    withTenant(`${CONTEXT}/documents/${encodeURIComponent(filename)}`, tenant),
    { suppressTenantHeader: true },
  );
  if (!res.ok) throw await parseApiError(res);
  return res.blob();
}

// ── Site Manager (website-urls / website-site / kb-status / triggers) ───────
// Nine tenant-admin endpoints (a superadmin and a customer-admin of the owning
// customer both qualify). ⛔ Only reindexWebsite and reloadWebsite SPEND, so they
// are the only ones answering 402 and 403 TENANT_RETIRED; everything else —
// promote/discard included — keeps working on a retired or empty-wallet
// tenant, because editing the URL list is a REPAIR operation.

/** The six imperative PATCH operations, in contract order. */
export const URL_DELTA_KEYS = ['pin', 'unpin', 'exclude', 'unexclude', 'add', 'remove'];

/**
 * The URL table. → { site, etag, draft_pending, total, limit, offset, urls[],
 * exclusions_error, exclusions_edit_blocked }. Rows arrive pinned → excluded →
 * file order: do NOT re-sort. `total` is after ?q= and before offset/limit.
 */
export async function getWebsiteUrls({ tenant, q, limit, offset } = {}) {
  const res = await apiFetch(
    withQuery(`${CONTEXT}/website-urls`, { tenant, q, limit, offset }),
    { suppressTenantHeader: true },
  );
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * Apply a delta under If-Match. Only NON-EMPTY operations are sent.
 * → { etag, applied, summary, urls_written, exclusions_written }
 * ⛔ `applied` keys are PAST TENSE (pinned/unpinned/added/removed/excluded/
 * unexcluded) — you send `pin`, you read back `pinned`. See site/urlDelta.js.
 * 412 CONTEXT_ETAG_MISMATCH → nothing written, reload the table and re-apply.
 * 409 KB_JOB_ACTIVE → the list is locked while a job runs; poll kb-status.
 */
export async function patchWebsiteUrls({ tenant, etag, delta }) {
  const body = {};
  URL_DELTA_KEYS.forEach((key) => {
    if (Array.isArray(delta?.[key]) && delta[key].length > 0) body[key] = delta[key];
  });
  const res = await apiFetch(withTenant(`${CONTEXT}/website-urls`, tenant), {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: etag ? { 'If-Match': etag } : {},
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** The connected site. → { site, is_sentinel, etag } — etag is null when the tenant owns no brand-guidelines file. */
export async function getWebsiteSite({ tenant }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/website-site`, tenant), {
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * [Modifica] the connected site. If-Match is OPTIONAL (absent = last-write-wins,
 * never a 428). → { site, is_sentinel, halves_written, rebaselined }.
 * ⚠️ Changes nothing in the URL list or the index — the caller warns the user.
 */
export async function putWebsiteSite({ tenant, site, etag }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/website-site`, tenant), {
    method: 'PUT',
    body: JSON.stringify({ site }),
    headers: etag ? { 'If-Match': etag } : {},
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * The status chip. → { state, kind, started_at, shadow_pending }. `state` is a
 * CLOSED vocabulary — Ready | Discovering | Indexing | Needs review | Failed —
 * derived on every read; poll it while the page is open (site/useKbStatus.js).
 */
export async function getKbStatus({ tenant }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/kb-status`, tenant), {
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** [Reindicizza] — re-crawl the curated list. 202 { job_id, tenant_id, kind, status }. SPENDS. */
export async function reindexWebsite({ tenant }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/website-urls/reindex`, tenant), {
    method: 'POST',
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * [Ricarica contenuti] — rescan the site for new pages, then re-index. 202. SPENDS.
 * The body is OMITTED unless confirmReplace (unknown fields are a 422). The
 * first call answers 409 SITE_APEX_CHANGED when the site moved domain: the
 * caller renders a destructive confirm from err.details and only then re-posts
 * with confirmReplace=true. ⛔ Never auto-retry with the flag.
 */
export async function reloadWebsite({ tenant, confirmReplace = false }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/website-urls/reload`, tenant), {
    method: 'POST',
    ...(confirmReplace ? { body: JSON.stringify({ confirm_replace: true }) } : {}),
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** [Accetta comunque] — promote the waiting shadow. 200 { tenant_id, promoted, chunks_before, chunks_after }. Never re-crawls. 409 KB_SHADOW_EMPTY is permanent for that shadow. */
export async function promoteKbShadow({ tenant }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/kb-shadow/promote`, tenant), {
    method: 'POST',
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/** [Annulla ricostruzione] — drop the shadow. 200 { tenant_id, discarded, shadow_pending }. `discarded: false` is a SUCCESS (nothing to drop), not an error. */
export async function discardKbShadow({ tenant }) {
  const res = await apiFetch(withTenant(`${CONTEXT}/kb-shadow/discard`, tenant), {
    method: 'POST',
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

// ── Brand Voice (the "Personalità del brand" tab of the Brand Voice screen) ────
// GET is open to any member. A tenant with no voice of its own answers 200 with
// `source: "global"` (the neutral default) — ⛔ derive UI state from `source`, never
// from `id` (the write target). 404 CONTEXT_ENTITY_NOT_FOUND when there is none at all.
// `language` omitted = the base (Italian) half; an empty value is DROPPED (a blank
// `?language=` is a 422). Being member-gated, it keeps answering when the
// TenantAdmin-gated brand-identity document read 403s.
// ⛔ With `language: 'en'` a 404 CONTEXT_ENTITY_NOT_FOUND is NOT an error: the
// workspace owns its Italian voice and no English file, and the backend never
// falls back to the Italian.
export async function getBrandVoice({ tenant, language } = {}) {
  const res = await apiFetch(withQuery(`${CONTEXT}/brand-voice`, { tenant, language }), {
    suppressTenantHeader: true,
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

// Structured write: { label, axes: {axisKey: guidance} }. `version` / `is_default` are
// server-set — never sent. ⚠️ 503 SERVICE_UNAVAILABLE is checked BEFORE If-Match (nothing
// written; re-GET before retrying) and this route does NOT check RUN_ACTIVE (the screen
// locks the voice itself during an AI update). ⛔ The voice etag is its OWN — never the
// brand-identity document's. Refusals: 412 CONTEXT_ETAG_MISMATCH, 422
// CONTEXT_ENTITY_INVALID (fewer than 3 axes, or a conflicting ★ file), 409
// CONTEXT_BASE_REQUIRED (English before Italian).
// → { id, language, source, etag, created, strict_mode_activated }.
// ⚠️ A save STAMPS the voice `human` (frozen — the AI stops updating it) on the row of
// the LANGUAGE written, yet the answer carries no `stamped` (a failed stamp is only
// logged server-side): read the "unstamped" verdict from the DOCUMENT re-read's
// `voice` block, never from this response.
export async function putBrandVoice({ tenant, language, etag, label, axes }) {
  const res = await apiFetch(withQuery(`${CONTEXT}/brand-voice`, { tenant, language }), {
    method: 'PUT',
    suppressTenantHeader: true,
    headers: etag ? { 'If-Match': etag } : {},
    body: JSON.stringify({ label, axes }),
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}
